import React, { useState } from 'react';
import useQuranReader from '../hooks/useQuranReader';
import { deepLinkUrl } from '../utils/deepLink';

const VerseDisplay = () => {
  const { 
    loading, 
    error, 
    verseData, 
    currentVerse, 
    showArabic, 
    showTransliteration, 
    showTranslation,
    playAudio,
    isPlaying,
    isAutoPlaying,
    toggleAutoPlay,
    isCurrentVerseSajda,
    getCurrentSajdaDetails,
    goToNextVerse,
    goToPreviousVerse,
    isAtFirstVerse,
    isAtLastVerse,
    currentSurah
  } = useQuranReader();

  const [shareLabel, setShareLabel] = useState(null);

  // The address bar already carries the verse; this makes that shareable in one
  // tap. navigator.share is the native sheet on mobile, clipboard is the
  // fallback everywhere else.
  const shareVerse = async () => {
    const url = deepLinkUrl(currentSurah, currentVerse);
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Quran Reader', url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareLabel('Link copied');
    } catch (shareError) {
      // A cancelled share sheet lands here too, so stay quiet unless nothing worked
      if (shareError && shareError.name === 'AbortError') return;
      setShareLabel('Copy failed');
    }
    setTimeout(() => setShareLabel(null), 2000);
  };

  const verseLabel = currentVerse === 0 ? "Bismillah" : `Verse ${currentVerse}`;
  const isSajda = !loading && !error && currentVerse > 0 && isCurrentVerseSajda();
  const sajdaDetails = isSajda ? getCurrentSajdaDetails() : null;

  return (
    <div className="space-y-6">
      {/* Top bar: verse label + sajda + audio controls */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center flex-wrap gap-2">
          <span 
            className="px-3 py-1 rounded-full text-sm font-medium font-lato"
            style={{ backgroundColor: 'var(--color-verse-label-bg)', color: 'var(--color-verse-label-text)' }}
          >
            {verseLabel}
          </span>
          
          {isSajda && (
            <div className="flex items-center bg-light-gold text-deep-green px-3 py-1 rounded-full text-sm font-medium font-lato">
              <span className="mr-1 text-deep-green text-xl">۩</span>
              <span>{sajdaDetails?.obligatory ? 'Obligatory Sajda' : 'Recommended Sajda'}</span>
            </div>
          )}
        </div>

        {/* Audio controls */}
        <div className="flex items-center gap-2">
          {/* Play/Pause button */}
          <button 
            onClick={playAudio}
            className="p-2.5 rounded-full transition-colors bg-light-gray text-heading hover:bg-light-gold focus-visible:bg-light-gold"
            aria-label={isPlaying ? "Pause audio" : "Play audio"}
          >
            {isPlaying ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25Zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25Z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z" clipRule="evenodd" />
              </svg>
            )}
          </button>

          {/* Share this verse */}
          <button
            onClick={shareVerse}
            className="p-2.5 rounded-full transition-colors bg-light-gray text-heading hover:bg-light-gold focus-visible:bg-light-gold"
            aria-label="Share this verse"
            title="Share this verse"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
            </svg>
          </button>

          {/* Autoplay button */}
          <button 
            onClick={toggleAutoPlay}
            className={`p-2.5 rounded-full transition-colors relative ${
              isAutoPlaying
                ? 'bg-button-bg text-button-text'
                : 'bg-light-gray text-heading hover:bg-light-gold focus-visible:bg-light-gold'
            }`}
            aria-label={isAutoPlaying ? "Stop continuous play" : "Start continuous play"}
            title={isAutoPlaying ? "Stop continuous play" : "Continuous play"}
          >
            {/* Repeat/continuous icon: a play icon with a circular arrow */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
            </svg>
            {/* Active indicator dot */}
            {isAutoPlaying && (
              <span 
                className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2"
                style={{ backgroundColor: 'var(--color-gold)', borderColor: 'var(--color-button-bg)' }}
              ></span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile-friendly navigation arrows + verse content */}
      <div className="flex items-stretch gap-2">
        {/* Left arrow - previous verse */}
        <button
          onClick={goToPreviousVerse}
          disabled={isAtFirstVerse}
          className="flex items-center justify-center px-2 sm:px-3 rounded-lg transition-colors shrink-0 self-center bg-light-gray text-heading enabled:hover:bg-light-gold enabled:focus-visible:bg-light-gold disabled:bg-transparent disabled:text-light-gray disabled:cursor-default"
          style={{ minHeight: '48px' }}
          aria-label="Previous verse"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z" clipRule="evenodd" />
          </svg>
        </button>

        {/* Verse content */}
        <div className="flex-1 min-w-0">
          {loading && (
            <div className="text-center py-10">
              <div
                className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent"
                style={{ borderColor: 'var(--color-heading)', borderRightColor: 'transparent' }}
              ></div>
              <p className="mt-2 font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.6 }}>Loading verse...</p>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-10 font-lato" style={{ color: 'var(--color-danger)' }}>{error}</div>
          )}

          {/* Arabic text */}
          {!loading && !error && showArabic && (
            <div className="text-right">
              <p className="leading-loose arabic-text" style={{ fontSize: 'var(--arabic-font-size)', color: 'var(--color-charcoal)' }} dir="rtl">
                {verseData?.arabic}
                {isSajda && <span className="text-gold mr-2">۩</span>}
              </p>
            </div>
          )}
          
          {/* Transliteration */}
          {!loading && !error && showTransliteration && (
            <div className="mt-4">
              <p className="italic font-lato" style={{ fontSize: 'var(--transliteration-font-size)', color: 'var(--color-charcoal)', opacity: 0.7 }}>
                {verseData?.transliteration}
              </p>
            </div>
          )}
          
          {/* Translation */}
          {!loading && !error && showTranslation && (
            <div className="mt-4">
              <p className="font-lato leading-relaxed" style={{ fontSize: 'var(--translation-font-size)', color: 'var(--color-charcoal)' }}>
                {verseData?.translation}
              </p>
            </div>
          )}
        </div>

        {/* Right arrow - next verse */}
        <button
          onClick={goToNextVerse}
          disabled={isAtLastVerse}
          className="flex items-center justify-center px-2 sm:px-3 rounded-lg transition-colors shrink-0 self-center bg-light-gray text-heading enabled:hover:bg-light-gold enabled:focus-visible:bg-light-gold disabled:bg-transparent disabled:text-light-gray disabled:cursor-default"
          style={{ minHeight: '48px' }}
          aria-label="Next verse"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {shareLabel && (
        <div
          className="text-center text-sm font-lato rounded-full py-1 px-3 mx-auto w-fit bg-light-gray text-heading"
          role="status"
        >
          {shareLabel}
        </div>
      )}

      {/* Autoplay indicator */}
      {isAutoPlaying && (
        <div 
          className="flex items-center justify-center gap-2 py-2 px-4 rounded-full mx-auto w-fit text-sm font-lato"
          style={{ backgroundColor: 'var(--color-light-gray)', color: 'var(--color-heading)' }}
        >
          <span className="relative flex h-2.5 w-2.5">
            <span 
              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
              style={{ backgroundColor: 'var(--color-gold)' }}
            ></span>
            <span 
              className="relative inline-flex rounded-full h-2.5 w-2.5"
              style={{ backgroundColor: 'var(--color-gold)' }}
            ></span>
          </span>
          Continuous play active
          <button 
            onClick={toggleAutoPlay} 
            className="ml-1 underline opacity-70 hover:opacity-100"
          >
            Stop
          </button>
        </div>
      )}
      
      {/* Sajda guidance message */}
      {isSajda && (
        <div 
          className="mt-6 p-4 rounded-md border"
          style={{ 
            backgroundColor: 'var(--color-soft-ivory)', 
            borderColor: 'var(--color-light-gold)' 
          }}
        >
          <p className="flex items-center text-heading font-lato">
            <span className="text-2xl mr-2 text-gold">۩</span>
            <span>
              {sajdaDetails?.obligatory 
                ? 'This verse requires an obligatory prostration (sajda) when read.' 
                : 'It is recommended to perform a prostration (sajda) when reading this verse.'}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default VerseDisplay;